import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useDialogContext } from '../../../../contexts/DialogContext';
import { useToastedMutation } from '../../../../ReactQuery';
import { post } from '../../../../redux/api';
import type { RegenerateBackupCodesResponse } from '../../../../../shared/types/routes/auth/mfa';

import { getMfaMethodsQueryKey } from './useListMfaMethods';

/**
 * Regenerate backup codes for the user's MFA method with confirmation dialog.
 */
export default function useRegenerateBackupCodes() {
  const queryClient = useQueryClient();
  const { onConfirm } = useDialogContext();

  const mutation = useToastedMutation({
    mutationFn: async () => {
      return post<never, RegenerateBackupCodesResponse>(
        '/api/auth/mfa/totp/backup-codes/regenerate',
        undefined as never,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getMfaMethodsQueryKey() });
    },
    successToastMessage: 'Backup codes regenerated successfully',
    errorToastMessage: 'Failed to regenerate backup codes',
  });

  const onRegenerateBackupCodes = () => {
    onConfirm({
      title: 'Confirm backup code regeneration',
      body: 'Are you sure you want to regenerate your backup codes? Your old codes will no longer work.',
      onConfirm: async () => {
        await mutation.mutateAsync();
      },
    });
  };

  const resetBackupCodes = useCallback(() => {
    mutation.reset();
  }, [mutation]);

  return {
    onRegenerateBackupCodes,
    resetBackupCodes,
    isRegenerating: mutation.isPending,
    backupCodes: mutation.data?.data?.backupCodes || [],
  };
}
